import React, { useState, useEffect } from "react";

const getSeoBadge = (score) => {
  if (score >= 80) return { badge: "🟢 Strong", color: "green" };
  if (score >= 50) return { badge: "🟡 Moderate", color: "orange" };
  return { badge: "🔴 Weak", color: "red" };
};

const getTrafficLight = (score, thresholds) => {
  if (typeof score !== "number" || isNaN(score)) return "⚪";

  // Binary (1 = pass)
  if ("green" in thresholds && Object.keys(thresholds).length === 1) {
    return score === thresholds.green ? "🟢" : "🔴";
  }

  // Binary check (1 = pass)
  if (thresholds.green === 1 && thresholds.yellow === undefined) {
    return score === 1 ? "🟢" : "🔴";
  }

  // Range check
  if (score >= thresholds.green) return "🟢";
  if (thresholds.yellow !== undefined && score >= thresholds.yellow) return "🟡";

  // Min/max range
  if ("min" in thresholds && "max" in thresholds) {
    return score >= thresholds.min && score <= thresholds.max ? "🟢" : "🔴";
  }

  // Min-only check
  if ("min" in thresholds) {
    return score >= thresholds.min ? "🟢" : "🔴";
  }

  // Fallback
  return "🔴";
};

const getCriticalIssues = ({ title, description, keyphrase, slug, body, alt }) => {
  const issues = [];

  if (!keyphrase) {
    issues.push("Missing focus keyphrase.");
    return issues;
  }

  if (!title.toLowerCase().includes(keyphrase.toLowerCase())) {
    issues.push("Focus keyphrase missing in title.");
  }

  if (!description.toLowerCase().includes(keyphrase.toLowerCase())) {
    issues.push("Focus keyphrase missing in meta description.");
  }

  if (!slug.includes(keyphrase.toLowerCase().replace(/\s+/g, "-"))) {
    issues.push("Focus keyphrase missing in URL slug.");
  }

  if (!body.toLowerCase().includes(keyphrase.toLowerCase())) {
    issues.push("Focus keyphrase missing in content body.");
  }

  const siteDomain = process.env.REACT_APP_SITE_DOMAIN || "forloveofwriters.com";
  const internalLinkRegex = new RegExp(`<a\\s+[^>]*href=["'][^"']*${siteDomain}[^"']*["']`, "i");
  const hasInternalLink = internalLinkRegex.test(body);
  if (!hasInternalLink) {
    issues.push("No internal link found.");
  }

  return issues;
};

export default function SeoAnalyser({
  seoTitle,
  metaDescription,
  focusKeyphrase,
  content,
  postSlug,
  caption,
  previousArticles,
  featuredImageUrl,
}) {
  const [seoScore, setSeoScore] = useState(0);
  const [hasInput, setHasInput] = useState(false);
  const [density, setDensity] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [trafficLights, setTrafficLights] = useState([]);
  const [detailedTrafficLights, setDetailedTrafficLights] = useState({});
  const [criticalIssues, setCriticalIssues] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [metricScores, setMetricScores] = useState({});

  const [keyphraseUnique, setKeyphraseUnique] = useState(false);
  const [keyphraseWordCount, setKeyphraseWordCount] = useState(0);
  const [isKeyphraseInTitle, setIsKeyphraseInTitle] = useState(false);
  const [isTitleStartingWithKeyphrase, setIsTitleStartingWithKeyphrase] = useState(false);
  const [isKeyphraseInMeta, setIsKeyphraseInMeta] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [isKeyphraseInContent, setIsKeyphraseInContent] = useState(false);
  const [isKeyphraseInFirstParagraph, setIsKeyphraseInFirstParagraph] = useState(false);
  const [h1Text, setH1Text] = useState("");
  const [isKeyphraseInH1, setIsKeyphraseInH1] = useState(false);
  const [subheadingsText, setSubheadingsText] = useState("");
  const [isKeyphraseInSubheadings, setIsKeyphraseInSubheadings] = useState(false);
  const [normalizedSlug, setNormalizedSlug] = useState("");
  const [isSlugExactMatch, setIsSlugExactMatch] = useState(false);
  const [isSlugPartialMatch, setIsSlugPartialMatch] = useState(false);
  const [firstParagraph, setFirstParagraph] = useState("");
  const [h1, setH1] = useState("");
  const [headings, setHeadings] = useState("");
  const [normalizedKeyphraseSlug, setNormalizedKeyphraseSlug] = useState("");
  const [slugWordCount, setSlugWordCount] = useState(0);
  const [imageCount, setImageCount] = useState(0);
  const [matchesImageName, setMatchesImageName] = useState(false);
  const [isAltTextOk, setIsAltTextOk] = useState(false);
  const [hasInternalLink, setHasInternalLink] = useState(false);
  const [hasExternalLink, setHasExternalLink] = useState(false);

  const stripHtml = (html) =>
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const title = (seoTitle || "").trim();
  const description = (metaDescription || "").trim();
  const keyphrase = (focusKeyphrase || "").trim();
  const body = (content || "").trim();
  const slug = (postSlug || "").trim();
  const alt = (caption || "").trim();

  const calculateSeoScore = () => {

    console.log("featuredImageUrl:", featuredImageUrl);

    const hasAnyInput =
      title !== "" ||
      description !== "" ||
      keyphrase !== "" ||
      stripHtml(body) !== "" ||
      slug !== "" ||
      alt !== "";

    setHasInput(hasAnyInput);

    if (!hasAnyInput) {
      setSeoScore(0);
      setSuggestions([]);
      setDensity(0);
      setWordCount(0);
      return;
    }

    const keyphraseUnique = !!keyphrase && !previousArticles?.some((article) => article.keyphrase?.toLowerCase() === keyphrase.toLowerCase());
    const keyphraseWordCount = keyphrase ? keyphrase.trim().split(/\s+/).length : 0;
    const isKeyphraseInTitle = keyphrase.trim().length > 0 && title.toLowerCase().includes(keyphrase.toLowerCase());
    const isTitleStartingWithKeyphrase = keyphrase.trim().length > 0 && typeof seoTitle === "string" && seoTitle.toLowerCase().startsWith(keyphrase.toLowerCase());
    const isKeyphraseInMeta = keyphrase.trim().length > 0 && description.toLowerCase().includes(keyphrase.toLowerCase());
    const plainText = body
      .replace(/<br\s*\/?>/gi, "\n")                 // treat <br> as line break
      .replace(/<\/p>/gi, "\n")                       // treat </p> as line break
      .replace(/<[^>]*>/g, "")                        // remove other tags
      .replace(/&nbsp;/g, " ")                        // decode spaces
      .replace(/\s+/g, " ")                           // collapse whitespace
      .trim();
    const computedWordCount = plainText ? plainText.split(" ").length : 0;
    const isKeyphraseInContent = keyphrase.trim().length > 0 && body.toLowerCase().includes(keyphrase.toLowerCase());
    const isKeyphraseInFirstParagraph = keyphrase.trim().length > 0 && content.split("</p>")[0].toLowerCase().includes(keyphrase.toLowerCase());
    const h1Text = (body.match(/<h1[^>]*>(.*?)<\/h1>/gi) || []).join(" ").toLowerCase();
    const isKeyphraseInH1 = keyphrase.trim().length > 0 && h1Text.includes(keyphrase.toLowerCase());
    const subheadingsText = (body.match(/<h[23][^>]*>(.*?)<\/h[23]>/gi) || []).join(" ").toLowerCase();
    const isKeyphraseInSubheadings = keyphrase.trim().length > 0 && subheadingsText.includes(keyphrase.toLowerCase());
    const normalizedSlug = keyphrase.trim().length > 0 && keyphrase.toLowerCase().trim().replace(/\s+/g, "-");
    const isSlugExactMatch = keyphrase.trim().length > 0 && slug === normalizedSlug;
    const isSlugPartialMatch = keyphrase.trim().length > 0 && slug.includes(normalizedSlug);
    const firstParagraph = content.split("</p>")[0];
    const h1 = (body.match(/<h1[^>]*>(.*?)<\/h1>/gi) || []).join(" ");
    const headings = (body.match(/<h[23][^>]*>(.*?)<\/h[23]>/gi) || []).join(" ").toLowerCase();
    const normalizedKeyphraseSlug = keyphrase.trim().length > 0 && keyphrase.toLowerCase().trim().replace(/\s+/g, "-");
    const slugWordCount = slug.split("-").filter(Boolean).length;
    const imageCount = (body.match(/<img/g) || []).length + (featuredImageUrl ? 1 : 0);
    const matchesImageName = keyphrase.trim().length > 0 &&
      (body.match(/<img[^>]+src=["']([^"']+)["']/gi) || []).some(imgTag => {
        const match = imgTag.match(/src=["']([^"']+)["']/i);
        if (!match) return false;
        const fileName = match[1].split('/').pop().toLowerCase();
        return fileName.includes(normalizedSlug);
    });
    const isAltTextOk = keyphrase.trim().length > 0 && alt.toLowerCase().includes(keyphrase.toLowerCase());
    const internalLinkRegex = new RegExp(`<a\\s+[^>]*href=["'][^"']*${(process.env.REACT_APP_SITE_DOMAIN || "forloveofwriters.com").replace(/\./g, "\\.")}[^"']*["']`, "i");
    const hasInternalLink = internalLinkRegex.test(body);
    const externalLinkRegex = new RegExp(`<a\\s+[^>]*href=["']https?:\/\/(?!${(process.env.REACT_APP_SITE_DOMAIN || "forloveofwriters.com").replace(/\./g, "\\.")})[^"']*["']`, "i");
    const hasExternalLink = externalLinkRegex.test(body);

    setKeyphraseUnique(keyphraseUnique);
    setKeyphraseWordCount(keyphraseWordCount);
    setIsKeyphraseInTitle(isKeyphraseInTitle);
    setIsTitleStartingWithKeyphrase(isTitleStartingWithKeyphrase);
    setIsKeyphraseInMeta(isKeyphraseInMeta);
    setWordCount(computedWordCount);
    setIsKeyphraseInContent(isKeyphraseInContent);
    setIsKeyphraseInFirstParagraph(isKeyphraseInFirstParagraph);
    setH1Text(h1Text);
    setIsKeyphraseInH1(isKeyphraseInH1);
    setSubheadingsText(subheadingsText);
    setIsKeyphraseInSubheadings(isKeyphraseInSubheadings);
    setNormalizedSlug(normalizedSlug);
    setIsSlugExactMatch(isSlugExactMatch);
    setIsSlugPartialMatch(isSlugPartialMatch);
    setFirstParagraph(firstParagraph);
    setH1(h1);
    setHeadings(headings);
    setNormalizedKeyphraseSlug(normalizedKeyphraseSlug);
    setSlugWordCount(slugWordCount);
    setImageCount(imageCount);
    setMatchesImageName(matchesImageName);
    setIsAltTextOk(isAltTextOk);
    setHasInternalLink(hasInternalLink);
    setHasExternalLink(hasExternalLink);

    if (!keyphrase) {
      setSuggestions(["Please enter a focus keyphrase."]);
    }

    let score = 0;
    const maxScore = 100;
    const suggestions = [];
    const metricScores = {}; // key: metric name, value: { score: X, max: Y }

    // Focus keyphrase is unique check
    if (keyphraseUnique) {
      score += 5;
      metricScores.keyphraseUnique = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseUnique = { score: 0, max: 5 };
      suggestions.push("Choose a unique focus keyphrase (ie. not previously used in another article).");
    }

    // Keyphrase is 3–5 words
    if (keyphraseWordCount >= 3 && keyphraseWordCount <= 5) {
      score += 5;
      metricScores.keyphraseLength = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseLength = { score: 0, max: 5 };
      suggestions.push(`Your focus keyphrase contains ${keyphraseWordCount} word${keyphraseWordCount !== 1 ? 's' : ''}. It should contain 3 to 5 words.`);
    }

    // Focus keyphrase in title
    if (isKeyphraseInTitle) {
      score += 5;
      metricScores.keyphraseInTitle = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInTitle = { score: 0, max: 5 };
      suggestions.push("Include the focus keyphrase in the title.");
    }

    // Keyphrase starts the SEO title
    if (isTitleStartingWithKeyphrase) {
      score += 5;
      metricScores.titleStartsWithKeyphrase = { score: 5, max: 5 };
    } else {
      metricScores.titleStartsWithKeyphrase = { score: 0, max: 5 };
      suggestions.push("Start your SEO title with the focus keyphrase.");
    }

    // Title length check
    if (title.length >= 40 && title.length <= 60) {
      score += 5;
      metricScores.titleLength = { score: 5, max: 5 };
    } else if (title.length < 40) {
      metricScores.titleLength = { score: 0, max: 5 };
      suggestions.push(`Your title is currently ${title.length} characters long. Increase it to 40-60 characters.`);
    } else {
      metricScores.titleLength = { score: 0, max: 5 };
      suggestions.push(`Your title is currently ${title.length} characters long. Decrease it to 40-60 characters.`);
    }

    // Focus keyphrase in meta description
    if (isKeyphraseInMeta) {
      score += 5;
      metricScores.keyphraseInMeta = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInMeta = { score: 0, max: 5 };
      suggestions.push("Include the focus keyphrase in the meta description.");
    }

    // Meta description length check
    if (description.length >= 120 && description.length <= 156) {
      score += 5;
      metricScores.metaLength = { score: 5, max: 5 };
    } else if (description.length < 120) {
      metricScores.metaLength = { score: 0, max: 5 };
      suggestions.push(`Your meta description is currently ${description.length} characters long. Increase it to 120-156 characters.`);
    } else {
      metricScores.metaLength = { score: 0, max: 5 };
      suggestions.push(`Your meta description is currently ${description.length} characters long. Decrease it to 120-156 characters.`);
    }

    // Content length check
    if (computedWordCount >= 500) {
      score += 5;
      metricScores.wordCount = { score: 5, max: 5 };
    } else {
      metricScores.wordCount = { score: 0, max: 5 };
      suggestions.push("Article must be at least 500 words in length.");
    }

    // Focus keyphrase in content
    if (isKeyphraseInContent) {
      score += 5;
      metricScores.keyphraseInContent = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInContent = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the content body.");
    }

    // Focus keyphrase density check
    if (computedWordCount === 0 || !keyphrase.trim()) {
      setDensity(0);
      suggestions.push("Add content to your article body.");
    } else {
      const escapedKeyphrase = keyphrase.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const keyphraseRegex = new RegExp(`\\b${escapedKeyphrase}\\b`, "gi");
      const keyphraseCount = (plainText.match(keyphraseRegex) || []).length;

      const density = (keyphraseCount / computedWordCount) * 100;
      setDensity(density);

      if (density >= 0.5 && density <= 2.5) {
        score += 5;
        metricScores.keyphraseDensity = { score: 5, max: 5 };
      } else if (density > 2.5) {
        metricScores.keyphraseDensity = { score: 0, max: 5 };
        suggestions.push(`Keyphrase density is too high (${density.toFixed(2)}%). Consider reducing the frequency of your focus keyphrase to avoid keyword stuffing.`);
      } else {
        metricScores.keyphraseDensity = { score: 0, max: 5 };
        suggestions.push(`Keyphrase density is too low (${density.toFixed(2)}%). Consider including your focus keyphrase more frequently.`);
      }
    }

    // Focus keyphrase in first paragraph
    if (isKeyphraseInFirstParagraph) {
      score += 5;
      metricScores.keyphraseInFirstParagraph = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInFirstParagraph = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the first paragraph.");
    }

    // Keyphrase in H1
    if (isKeyphraseInH1) {
      score += 5;
      metricScores.keyphraseInH1 = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseInH1 = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the H1 heading.");
    }

    // Headings check & Focus keyphrase in subheadings
    if (!headings) {
      metricScores.keyphraseInSubheadings = { score: 0, max: 5 };
      suggestions.push("Use H2 or H3 subheadings that include your focus keyphrase.");
    } else if (!isKeyphraseInSubheadings) {
      metricScores.keyphraseInSubheadings = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in at least one H2 or H3 subheading.");
    } else {
      score += 5;
      metricScores.keyphraseInSubheadings = { score: 5, max: 5 };
    }

    // URL slug check
    if (isSlugExactMatch) {
      score += 5;
      metricScores.slugUrl = { score: 5, max: 5 };
    } else if (isSlugPartialMatch) {
      score += 3;
      metricScores.slugUrl = { score: 3, max: 5 };
      suggestions.push("Make your slug match the focus keyphrase exactly, if possible.");
    } else {
      metricScores.slugUrl = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the URL slug. If possible, make it match the focus keyphrase exactly.");
    }

    // Slug length is 3–5 words
    if (slugWordCount >= 3 && slugWordCount <= 5) {
      score += 5;
      metricScores.slugLength = { score: 5, max: 5 };
    } else {
      metricScores.slugLength = { score: 0, max: 5 };
      suggestions.push("Use a short slug of 3–5 words for better readability and SEO.");
    }

    // Image count check
    if (imageCount >= Math.max(1, Math.floor(wordCount / 500))) {
      score += 5;
      metricScores.imageCount = { score: 5, max: 5 };
    } else {
      metricScores.imageCount = { score: 0, max: 5 };
      suggestions.push("Insert at least one image for every 500 words (including the featured image).");
    }

    // Image filename includes keyphrase
    if (matchesImageName) {
      score += 5;
      metricScores.keyphraseImageFilename = { score: 5, max: 5 };
    } else {
      metricScores.keyphraseImageFilename = { score: 0, max: 5 };
      suggestions.push("Include the focus keyphrase in your image filename.");
    }

    // Image alt text check
    if (isAltTextOk) {
      score += 5;
      metricScores.imageAltText = { score: 5, max: 5 };
    } else {
      metricScores.imageAltText = { score: 0, max: 5 };
      suggestions.push("Include your focus keyphrase in the image alt text.");
    }

    // Internal link check
    if (hasInternalLink) {
      score += 5;
      metricScores.internalLink = { score: 5, max: 5 };
    } else {
      metricScores.internalLink = { score: 0, max: 5 };
      suggestions.push("Add at least one internal link to your content.");
    }

    // External link check
    if (hasExternalLink) {
      score += 5;
      metricScores.externalLink = { score: 5, max: 5 };
    } else {
      metricScores.externalLink = { score: 0, max: 5 };
      suggestions.push("Add at least one external link to your content.");
    }

    setSeoScore(Math.round(Math.max(0, Math.min(score, maxScore))));

    setSuggestions(suggestions);
    setMetricScores(metricScores);

    const minImages = Math.max(1, Math.floor(computedWordCount / 500));

    const thresholds = {
      keyphraseUnique: { green: 1 },
      keyphraseLength: { min: 3, max: 5 }, // Ideal: 3–5
      keyphraseInTitle: { green: 1 },
      titleStartsWithKeyphrase: { green: 1 },
      titleLength: { min: 40, max: 60 },
      keyphraseInMeta: { green: 1 },
      metaLength: { min: 120, max: 156 },
      wordCount: { min: 500 },
      keyphraseInContent: { green: 1 },
      keyphraseDensity: { min: 0.5, max: 2.5 },
      keyphraseInFirstParagraph: { green: 1 },
      keyphraseInH1: { green: 1 },
      keyphraseInSubheadings: { green: 1 },
      slugUrl: { green: 1 },
      slugLength: { min: 3, max: 5 },
      imageCount: { min: minImages },
      keyphraseImageFilename: { green: 1 },
      imageAltText: { green: 1 },
      internalLink: { green: 1 },
      externalLink: { green: 1 },
    };

    const inputValues = {
      keyphraseUnique: Number(keyphraseUnique),
      keyphraseLength: keyphraseWordCount,
      keyphraseInTitle: Number(isKeyphraseInTitle),
      titleStartsWithKeyphrase: Number(isTitleStartingWithKeyphrase),
      titleLength: title.length,
      keyphraseInMeta: Number(isKeyphraseInMeta),
      metaLength: description.length,
      wordCount: computedWordCount,
      keyphraseInContent: Number(isKeyphraseInContent),
      keyphraseDensity: density,
      keyphraseInFirstParagraph: Number(isKeyphraseInFirstParagraph),
      keyphraseInH1: Number(isKeyphraseInH1),
      keyphraseInSubheadings: Number(isKeyphraseInSubheadings),
      slugUrl: Number(isSlugExactMatch || isSlugPartialMatch),
      slugLength: slugWordCount,
      imageCount: imageCount,
      keyphraseImageFilename: Number(matchesImageName),
      imageAltText: Number(isAltTextOk),
      internalLink: Number(hasInternalLink),
      externalLink: Number(hasExternalLink),
    };

    const seoTrafficLights = Object.fromEntries(
      Object.entries(inputValues).map(([key, value]) => [
        key,
        getTrafficLight(value, thresholds[key]),
      ])
    );

    setDetailedTrafficLights(seoTrafficLights);
    setTrafficLights(Object.values(seoTrafficLights));

    const criticalIssues = getCriticalIssues({
      title,
      description,
      keyphrase,
      slug,
      body,
      alt,
    });

    setCriticalIssues(criticalIssues);
  };

  useEffect(() => {
    calculateSeoScore();
  }, [title, description, keyphrase, body, slug, alt]);

  const summary = getSeoBadge(seoScore);

  return (
    <div className="seo-score-container">
      <div style={{ marginTop: "10px", marginBottom: "10px", fontWeight: "bold", fontSize: "16px", color: "#000" }}>
        SEO Score:{" "}
        {hasInput ? (
          <span style={{ color: summary.color }}>
            {summary.badge} ({seoScore}%)
          </span>
        ) : (
          <span style={{ fontStyle: "italic", fontSize: "16px", fontWeight: "normal", color: "#555" }}>
            Start adding content to see your SEO score.
          </span>
        )}
        {hasInput && suggestions.some(s => s.toLowerCase().includes("missing") || s.toLowerCase().includes("include")) && (
          <li style={{ listStyleType: "none", fontWeight: "bold", margin: "10px" }}>
            ⚠️ Critical Issues Found – Must be addressed before publishing:
          </li>
        )}
        {criticalIssues.length > 0 && (
          <ul>
            <div style={{ fontWeight: "normal", marginTop: "10px" }}>
              {criticalIssues.map((issue, i) => (
                <li key={i}>{issue}</li>
              ))}
            </div>
          </ul>
        )}
      </div>
      {hasInput && density > 0 && (
        <div style={{ fontStyle: "italic", marginTop: "10px", marginBottom: "10px" }}>
          Keyphrase density: {density.toFixed(2)}%
        </div>
      )}
      {hasInput && (
      <div className="seo-recommendations">
      <li style={{ listStyleType: "none", fontWeight: "bold", margin: "10px" }}>
        Scorecard:
      </li>
      <ul style={{ listStyleType: "none" }}>

        {/* Keyphrase is unique (ie. not previously used in another article) */}
        <li>{detailedTrafficLights.keyphraseUnique ?? "⚪"} Focus Keyphrase is unique: {keyphraseUnique ? "Yes" : "No"} – Score: {metricScores.keyphraseUnique?.score ?? 0}/{metricScores.keyphraseUnique?.max ?? 5}</li>

        {/* Keyphrase is 3–5 words */}
        <li>{detailedTrafficLights.keyphraseLength ?? "⚪"} Focus Keyphrase Length: {keyphraseWordCount} words (Ideal: 3–5) – Score: {metricScores.keyphraseLength?.score ?? 0}/{metricScores.keyphraseLength?.max ?? 5}</li>

        {/* Focus keyphrase in title */}
        <li>{detailedTrafficLights.keyphraseInTitle ?? "⚪"} Focus Keyphrase in Title: {isKeyphraseInTitle ? "Yes" : "No"} – Score: {metricScores.keyphraseInTitle?.score ?? 0}/{metricScores.keyphraseInTitle?.max ?? 5}</li>

        {/* Title starts with keyphrase */}
        <li>{detailedTrafficLights.titleStartsWithKeyphrase ?? "⚪"} Title Starts with Keyphrase: {isTitleStartingWithKeyphrase ? "Yes" : "No"} – Score: {metricScores.titleStartsWithKeyphrase?.score ?? 0}/{metricScores.titleStartsWithKeyphrase?.max ?? 5}</li>

        {/* Title length check */}
        <li>{detailedTrafficLights.titleLength ?? "⚪"} Title Length: {title.length} characters (Ideal: 40–60) – Score: {metricScores.titleLength?.score ?? 0}/{metricScores.titleLength?.max ?? 5}</li>

        {/* Focus keyphrase in meta description */}
        <li>{detailedTrafficLights.keyphraseInMeta ?? "⚪"} Focus Keyphrase in Meta Description: {isKeyphraseInMeta ? "Yes" : "No"} – Score: {metricScores.keyphraseInMeta?.score ?? 0}/{metricScores.keyphraseInMeta?.max ?? 5}</li>

        {/* Meta description length check */}
        <li>{detailedTrafficLights.metaLength ?? "⚪"} Meta Description Length: {description.length} characters (Ideal: 120–156) – Score: {metricScores.metaLength?.score ?? 0}/{metricScores.metaLength?.max ?? 5}</li>

        {/* Content length check */}
        <li>{detailedTrafficLights.wordCount ?? "⚪"} Word Count: {wordCount} (Minimum: 500) – Score: {metricScores.wordCount?.score ?? 0}/{metricScores.wordCount?.max ?? 5}</li>

        {/* Focus keyphrase in content */}
        <li>{detailedTrafficLights.keyphraseInContent ?? "⚪"} Focus Keyphrase in Body Content: {isKeyphraseInContent ? "Yes" : "No"} – Score: {metricScores.keyphraseInContent?.score ?? 0}/{metricScores.keyphraseInContent?.max ?? 5}</li>

         {/* Focus keyphrase density check */}
        <li>{detailedTrafficLights.keyphraseDensity ?? "⚪"} Keyphrase Density: {density.toFixed(2)}%  (Ideal: 0.5%–2.5%) – Score: {metricScores.keyphraseDensity?.score ?? 0}/{metricScores.keyphraseDensity?.max ?? 5}</li>

        {/* Focus keyphrase in first paragraph */}
        <li>{detailedTrafficLights.keyphraseInFirstParagraph ?? "⚪"} Focus Keyphrase in First Paragraph: {isKeyphraseInFirstParagraph ? "Yes" : "No"} – Score: {metricScores.keyphraseInFirstParagraph?.score ?? 0}/{metricScores.keyphraseInFirstParagraph?.max ?? 5}</li>

        {/* Keyphrase in H1 */}
        <li>{detailedTrafficLights.keyphraseInH1 ?? "⚪"} Focus Keyphrase in H1 Heading: {isKeyphraseInH1 ? "Yes" : "No"} – Score: {metricScores.keyphraseInH1?.score ?? 0}/{metricScores.keyphraseInH1?.max ?? 5}</li>

        {/* Headings check & Focus keyphrase in subheadings */}
        <li>{detailedTrafficLights.keyphraseInSubheadings ?? "⚪"} Focus Keyphrase in H2/H3 Subheadings: {isKeyphraseInSubheadings ? "Yes" : "No"} – Score: {metricScores.keyphraseInSubheadings?.score ?? 0}/{metricScores.keyphraseInSubheadings?.max ?? 5}</li>

        {/* URL slug check */}
        <li>{detailedTrafficLights.slugUrl ?? "⚪"} Slug Contains Keyphrase: {isSlugExactMatch || isSlugPartialMatch ? "Yes" : "No"} – Score: {metricScores.slugUrl?.score ?? 0}/{metricScores.slugUrl?.max ?? 5}</li>

        {/* Slug length is 3–5 words */}
        <li>{detailedTrafficLights.slugLength ?? "⚪"} Slug Word Count: {slugWordCount} words (Ideal: 3–5) – Score: {metricScores.slugLength?.score ?? 0}/{metricScores.slugLength?.max ?? 5}</li>

        {/* Image count check */}
        <li>{detailedTrafficLights.imageCount ?? "⚪"} Image Count: {imageCount} (Recommended: ≥ {Math.max(1, Math.floor(wordCount / 500))}) – Score: {metricScores.imageCount?.score ?? 0}/{metricScores.imageCount?.max ?? 5}</li>

        {/* Image filename includes keyphrase */}
        <li>{detailedTrafficLights.keyphraseImageFilename ?? "⚪"} Image Filename Contains Keyphrase: {matchesImageName ? "Yes" : "No"} – Score: {metricScores.keyphraseImageFilename?.score ?? 0}/{metricScores.keyphraseImageFilename?.max ?? 5}</li>

        {/* Image alt text check */}
        <li>{detailedTrafficLights.imageAltText ?? "⚪"} Image Alt Text Contains Keyphrase: {isAltTextOk ? "Yes" : "No"} – Score: {metricScores.imageAltText?.score ?? 0}/{metricScores.imageAltText?.max ?? 5}</li>

        {/* Internal link check */}
        <li>{detailedTrafficLights.internalLink ?? "⚪"} Internal Link Present: {hasInternalLink ? "Yes" : "No"} – Score: {metricScores.internalLink?.score ?? 0}/{metricScores.internalLink?.max ?? 5}</li>

        {/* External link check */}
        <li>{detailedTrafficLights.externalLink ?? "⚪"} External Link Present: {hasExternalLink ? "Yes" : "No"} – Score: {metricScores.externalLink?.score ?? 0}/{metricScores.externalLink?.max ?? 5}</li>
      </ul>

      {suggestions.length > 0 && (
        <>
          <li style={{ listStyleType: "none", fontWeight: "bold", margin: "10px" }}>
            Suggestions:
          </li>
          <ul style={{ listStyleType: "none" }}>
            {suggestions.map((r, i) => (
              <li key={i}>💡 {r}</li>
            ))}
          </ul>
        </>
      )}
      </div>
      )}
    </div>
  );
}
